import * as Yup from "yup";
import { getCustomFieldValidations } from "./customYupSchemas";
import { evaluateRuleById } from "./utils";
import { returnCustomFieldsFromSchema } from "../../../../services/custom-fields";
const ARRAY_METADATA = /* @__PURE__ */ Symbol("arrayMetadata");
const itemSchemaCache = /* @__PURE__ */ new WeakMap();
const buildItemSchema = (shape) => {
  const cached = itemSchemaCache.get(shape);
  if (cached) return cached;
  const built = Yup.object(
    Object.fromEntries(
      Object.entries(shape).map(([k, v]) => [
        k,
        Yup.isSchema(v) ? v : buildItemSchema(v)
      ])
    )
  );
  itemSchemaCache.set(shape, built);
  return built;
};
const getRequiredVisibleFields = (field, formData, rules, preview) => {
  const requiredVisibleFields = [];
  const evaluateVisibility = (field2) => {
    const { visible, visible_check } = field2;
    const handlerResult = visible_check?.handler ? evaluateRuleById(visible_check.handler, formData, rules, preview) : void 0;
    if (!visible_check?.handler) {
      return visible !== false;
    }
    if (visible === true && handlerResult === true) return false;
    if (visible === true && handlerResult === false) return true;
    if (visible === false && handlerResult === true) return true;
    return false;
  };
  const path = field?.encompass_ids?.[0]?.modelPath;
  if (!path) {
    field?.fields?.forEach((subField) => {
      const result = getRequiredVisibleFields(
        subField,
        formData,
        rules,
        preview
      );
      requiredVisibleFields.push(...result);
    });
  }
  const isVisible = evaluateVisibility(field);
  const isRequired = field.required;
  if (isVisible && isRequired && !field.disabled) {
    requiredVisibleFields.push({ path, label: field.label || path });
  }
  return requiredVisibleFields;
};
const getRequiredVisibleFieldsFromSchema = (schema, rules, formData, preview) => {
  const requiredFields = [];
  const evaluateVisibility = (field) => {
    const { visible, visible_check } = field;
    const handlerResult = visible_check?.handler ? evaluateRuleById(visible_check.handler, formData, rules, preview) : void 0;
    if (!visible_check?.handler) {
      return visible !== false;
    }
    if (visible === true && handlerResult === true) return false;
    if (visible === true && handlerResult === false) return true;
    if (visible === false && handlerResult === true) return true;
    return false;
  };
  const screens = schema.schema.screens[0].screens;
  screens.forEach((screen) => {
    ["VISIBLE", "DETAILED"].forEach((sectionName) => {
      const section = schema.schema.screens.find((s) => s.id === screen.id)?.fields?.find((f) => f.name === sectionName)?.fields ?? [];
      section.forEach((field) => {
        const result = getRequiredVisibleFields(
          field,
          formData,
          rules,
          preview
        );
        requiredFields.push(...result);
      });
    });
  });
  return requiredFields;
};
const setInShape = (obj, pathParts, schema, fullPath) => {
  const key = pathParts[0];
  const arrayMatch = key.match(/^(.+)\[(\d+)\]$/);
  if (arrayMatch) {
    const [, arrayName, arrayIndex] = arrayMatch;
    const index = parseInt(arrayIndex, 10);
    if (!obj[ARRAY_METADATA]) {
      obj[ARRAY_METADATA] = {};
    }
    if (!obj[ARRAY_METADATA][arrayName]) {
      obj[ARRAY_METADATA][arrayName] = {};
    }
    const metadata = obj[ARRAY_METADATA][arrayName];
    if (!metadata[index]) {
      metadata[index] = {};
    }
    if (pathParts.length > 1) {
      setInShape(metadata[index], pathParts.slice(1), schema, fullPath);
    }
    if (!obj[arrayName]) {
      obj[arrayName] = Yup.lazy((value) => {
        if (!Array.isArray(value)) {
          return Yup.mixed().nullable();
        }
        return Yup.array().test(
          "array-index-validation",
          "",
          function(arrayValue) {
            if (!Array.isArray(arrayValue)) return true;
            const errors = [];
            Object.entries(metadata).forEach(
              ([idx, itemShape]) => {
                const itemIndex = parseInt(idx, 10);
                if (arrayValue[itemIndex] === void 0) return;
                const itemSchema = buildItemSchema(itemShape);
                try {
                  itemSchema.validateSync(arrayValue[itemIndex], {
                    abortEarly: false
                  });
                } catch (err) {
                  if (err.inner && err.inner.length > 0) {
                    err.inner.forEach((innerErr) => {
                      errors.push({
                        path: `${arrayName}[${itemIndex}].${innerErr.path}`,
                        message: innerErr.message
                      });
                    });
                  } else {
                    errors.push({
                      path: `${arrayName}[${itemIndex}]`,
                      message: err.message
                    });
                  }
                }
              }
            );
            if (errors.length > 0) {
              throw new Yup.ValidationError(
                errors.map(
                  (error) => new Yup.ValidationError(
                    error.message,
                    arrayValue,
                    error.path
                  )
                )
              );
            }
            return true;
          }
        );
      });
    }
    return;
  }
  if (pathParts.length === 1) {
    obj[key] = schema;
    return;
  }
  if (!obj[key]) {
    obj[key] = {};
  }
  setInShape(obj[key], pathParts.slice(1), schema, fullPath);
};
const applyRequiredToCustomValidations = (customFieldValidations, requiredFields) => {
  requiredFields.forEach((requiredField) => {
    const { path, label } = requiredField;
    const customValidation = customFieldValidations.find(
      (validation) => validation.path === path
    );
    if (customValidation) {
      customValidation.validation = customValidation.validation.concat(
        Yup.mixed().nullable().required(`${label} is required`)
      );
    } else {
      customFieldValidations.push({
        path,
        label,
        validation: Yup.mixed().nullable().required(`${label} is required`)
      });
    }
  });
  return customFieldValidations;
};
const generateSubmissionSchema = (schema, preview, formValues, loanTerms) => {
  const rawShape = {};
  const customFieldValidations = getCustomFieldValidations(
    formValues,
    loanTerms
  );
  const requiredFields = getRequiredVisibleFieldsFromSchema(
    schema,
    schema?.schemaRules,
    formValues(),
    preview
  );
  const allValidations = applyRequiredToCustomValidations(
    customFieldValidations,
    requiredFields
  );
  for (const { path, validation } of allValidations) {
    const pathParts = path.split(".");
    setInShape(rawShape, pathParts, validation, path);
  }
  const toYupShape = (obj) => Yup.object(
    Object.fromEntries(
      Object.entries(obj).map(([key, val]) => [
        key,
        Yup.isSchema(val) ? val : toYupShape(val)
      ])
    )
  );
  return toYupShape(rawShape);
};
const seedUnmappedCustomFieldsFromResponse = (schemaForm) => {
  const additionalFields = schemaForm.getValues("lockRequestAdditionalFields");
  if (!Array.isArray(additionalFields) || additionalFields.length === 0) {
    return;
  }
  const seeded = {
    ...schemaForm.getValues("unmappedLockRequestAdditionalFields") ?? {}
  };
  let changed = false;
  additionalFields.forEach((field) => {
    const key = field?.fieldId?.replaceAll(".", "*");
    if (key && !Object.prototype.hasOwnProperty.call(seeded, key)) {
      seeded[key] = field.value;
      changed = true;
    }
  });
  if (changed) {
    schemaForm.setValue("unmappedLockRequestAdditionalFields", seeded);
  }
};
const validateAndSubmitSearchForm = async ({
  schemaForm,
  schema,
  preview = false,
  loanTerms
}) => {
  seedUnmappedCustomFieldsFromResponse(schemaForm);
  const formData = schemaForm.getValues();
  const yupSchema = generateSubmissionSchema(
    schema,
    preview,
    schemaForm.getValues,
    loanTerms
  );
  schemaForm.clearErrors();
  try {
    await yupSchema.validate(formData, { abortEarly: false });
    transformFormDataCustomFieldsToResponseFormData(
      formData,
      schema.schema.screens
    );
    return { valid: true, formData, errorPaths: [] };
  } catch (validationError) {
    const isYupError = Array.isArray(validationError?.inner);
    if (!isYupError) {
      console.error(
        "Search form submission failed unexpectedly",
        validationError?.message,
        validationError?.stack
      );
    }
    const errors = isYupError ? validationError.inner : [];
    const errorMap = errors.reduce((acc, curr) => {
      acc[curr.path] = { message: curr.message };
      return acc;
    }, {});
    Object.entries(errorMap).forEach(([field, error]) => {
      schemaForm.setError(field, {
        type: "manual",
        message: error.message,
        shouldFocus: true
      });
    });
    return {
      valid: false,
      formData,
      errorPaths: Object.keys(errorMap)
    };
  }
};
const transformFormDataCustomFieldsToResponseFormData = (formData, screens) => {
  const flattenedCustomFields = returnCustomFieldsFromSchema(screens);
  const customFieldValues = formData.unmappedLockRequestAdditionalFields ?? null;
  if (customFieldValues) {
    const lockRequestAdditionalFields = Object.keys(customFieldValues).filter((key) => customFieldValues[key] !== void 0).map((key) => {
      const untransformedKey = key.replaceAll("*", ".");
      const matchingField = flattenedCustomFields.find(
        (field) => field?.encompass_ids?.[0]?.id === untransformedKey
      );
      const originField = formData.lockRequestAdditionalFields?.find(
        (field) => field.fieldId === untransformedKey
      );
      return {
        fieldId: untransformedKey,
        value: customFieldValues[key],
        description: matchingField?.label || originField?.description || key,
        dataType: matchingField?.encompass_ids?.[0]?.name ?? originField?.dataType
      };
    });
    formData.lockRequestAdditionalFields = lockRequestAdditionalFields;
    return formData;
  }
  return formData;
};
const deriveAccordionFieldMapFromSchema = (schema) => {
  const map = {};
  const screens = schema?.schema?.screens?.[0]?.screens || [];
  screens.forEach((screen, index) => {
    const detailedFields = schema.schema.screens?.find((s) => s.id === screen.id)?.fields?.find((f) => f.name === "DETAILED")?.fields || [];
    map[index] = detailedFields.map(
      (f) => f?.encompass_ids?.[0]?.modelPath
    );
  });
  return map;
};
const expandAccordionsByErrorPaths = (accordionFieldMap, errorPaths) => {
  return Object.entries(accordionFieldMap).reduce((acc, [index, paths]) => {
    if (paths.some((p) => errorPaths.includes(p))) {
      acc[+index] = true;
    }
    return acc;
  }, {});
};
const expandAllAccordions = (accordionFieldMap) => {
  return Object.entries(accordionFieldMap).reduce((acc, [index, paths]) => {
    if (paths.length > 0) {
      acc[+index] = true;
    }
    return acc;
  }, {});
};
const scrollToFirstError = (errorPaths, delayMs = 100) => {
  setTimeout(() => {
    const el = document.getElementById(errorPaths[0]);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
    el?.focus();
  }, delayMs);
};
export {
  deriveAccordionFieldMapFromSchema,
  expandAccordionsByErrorPaths,
  expandAllAccordions,
  scrollToFirstError,
  transformFormDataCustomFieldsToResponseFormData,
  validateAndSubmitSearchForm
};
